import { jsx } from "react/jsx-runtime";
import { useSelector, shallowEqual } from "react-redux";
import { isDisabledVip } from "@nxplatform/customer-noauth/slices/customer-noauth.slice";
import { selectIsNewRegistration } from "@nxplatform/customer-noauth/slices/customer-token.slice";
import { getCookie } from "@nxplatform/helpers/common";
import { btagKey } from "@nxplatform/platform-entities";
import { selectIsReferralProgramEnabled } from "@nxplatform/platform-settings/brand-settings.slice";
import { selectCustomAsidePages, allowedLinks } from "@nxplatform/platform-settings/locale-setup.routes";
import { isCountryFinland, isDisabledPromotions } from "@nxplatform/platform-settings/locale-setup.selectors";
import { selectPlatformSettingRegistrationWithBtag } from "@nxplatform/platform-settings/registration-settings.slice";
import { RegistrationPageWrapper as _RegistrationPageWrapper } from "../pages/registration-page-wrapper.js";
import { filterRoutes, mergeRoutes } from "./helpers.js";
import { selectIsTemplatesPageVisible, selectMobileAppVisibleElements, selectPlatformSettingsCasinoEnabled } from "@nxplatform/platform-settings/platform-settings.selectors";
const useCreateOuterRoutes = (templates, Container, additional) => {
  const {
    disabledPromotions,
    disabledVip,
    isCasinoEnabled,
    registerOnlyWithBtag,
    templatesVisible,
    newRegistration,
    referralProgramEnabled,
    isFin
  } = useSelector((state) => ({
    disabledPromotions: isDisabledPromotions(state),
    disabledVip: isDisabledVip(state),
    isCasinoEnabled: selectPlatformSettingsCasinoEnabled(state),
    registerOnlyWithBtag: selectPlatformSettingRegistrationWithBtag(state),
    mobileAppVisibleElements: selectMobileAppVisibleElements(state),
    templatesVisible: selectIsTemplatesPageVisible(state),
    newRegistration: selectIsNewRegistration(state),
    isAsideCustom: selectCustomAsidePages(window.location.pathname),
    referralProgramEnabled: selectIsReferralProgramEnabled(state),
    isFin: isCountryFinland(state)
  }), shallowEqual);
  if (!templates) return [];
  const {
    baseTemplates: BaseTemplates,
    hallOfFameSport: HallOfFameSport,
    pageRestore: PageRestore,
    pageUserConfirmation: PageUserConfirmation,
    pageNotFound: PageNotFound,
    pageContacts: PageContacts,
    promotions: Promotions,
    vipSportPrograms: VipSportPrograms,
    vipPrograms: VipPrograms,
    landings: Landings,
    registrationPage: RegistrationPage,
    registrationPageEsports: RegistrationPageEsports,
    bettorsTournament: BettorsTournament,
    hallOfFameCasino: HallOfFameCasino,
    mobileApp: MobileApp,
    tournaments: Tournaments,
    tournamentPage: TournamentPage,
    hallOfFame: HallOfFame,
    bonusPage: BonusPage,
    deepLink: DeepLinkPage,
    referrals: Referrals
  } = templates;
  const token = new URLSearchParams(window.location.search).get("token");
  const btagCookie = !!getCookie(btagKey);
  const registrationActive = () => registerOnlyWithBtag ? btagCookie : true;
  const getPromotionsCondition = () => !disabledPromotions || disabledPromotions && isFin;
  const showTemplatesPage = BaseTemplates && templatesVisible;
  const routes = [
    !!BettorsTournament && !disabledPromotions && {
      path: allowedLinks.bettorsTournament,
      element: /* @__PURE__ */ jsx(BettorsTournament, {})
    },
    !!PageContacts && {
      path: allowedLinks.contactUs,
      element: /* @__PURE__ */ jsx(PageContacts, {})
    },
    !!PageNotFound && {
      path: allowedLinks.notFound,
      element: /* @__PURE__ */ jsx(PageNotFound, {})
    },
    !!Promotions && !disabledPromotions && {
      path: allowedLinks.promotions,
      element: /* @__PURE__ */ jsx(Promotions, {})
    },
    !!VipSportPrograms && !disabledVip && {
      path: allowedLinks.vipSportPrograms,
      element: /* @__PURE__ */ jsx(VipSportPrograms, {})
    },
    !!VipPrograms && !disabledVip && isCasinoEnabled && {
      path: allowedLinks.vipPrograms,
      element: /* @__PURE__ */ jsx(VipPrograms, {})
    },
    !!Landings && {
      path: allowedLinks.landings,
      element: /* @__PURE__ */ jsx(Landings, {})
    },
    !!RegistrationPage && !!newRegistration && registrationActive() && [
      allowedLinks.registrationPage,
      allowedLinks.popanderRegistration
    ].map((path) => ({
      key: path,
      path,
      element: /* @__PURE__ */ jsx(_RegistrationPageWrapper, { RegistrationPage })
    })),
    {
      path: allowedLinks.passwordRestore,
      element: /* @__PURE__ */ jsx(PageRestore, {})
    },
    token && {
      path: allowedLinks.userConfirmation,
      element: /* @__PURE__ */ jsx(PageUserConfirmation, {})
    },
    !!HallOfFameCasino && isCasinoEnabled && {
      path: allowedLinks.hallOfFameCasino,
      element: /* @__PURE__ */ jsx(HallOfFameCasino, {})
    },
    !!HallOfFameSport && {
      path: allowedLinks.hallOfFameSport,
      element: /* @__PURE__ */ jsx(HallOfFameSport, {})
    },
    !!BonusPage && {
      path: allowedLinks.bonusPackage,
      element: /* @__PURE__ */ jsx(BonusPage, {})
    },
    !!MobileApp && {
      path: allowedLinks.mobileApp,
      element: /* @__PURE__ */ jsx(MobileApp, {})
    },
    !!BaseTemplates && showTemplatesPage && {
      path: allowedLinks.elements,
      element: /* @__PURE__ */ jsx(BaseTemplates, {})
    },
    !!RegistrationPageEsports && !!newRegistration && registrationActive() && {
      path: allowedLinks.registrationPageEsports,
      element: /* @__PURE__ */ jsx(RegistrationPageEsports, {})
    },
    !!DeepLinkPage && {
      path: allowedLinks.deepLink,
      element: /* @__PURE__ */ jsx(DeepLinkPage, {})
    },
    !!Referrals && referralProgramEnabled && {
      path: allowedLinks.referrals,
      element: /* @__PURE__ */ jsx(Referrals, {})
    },
    !!Tournaments && getPromotionsCondition() && {
      path: allowedLinks.tournaments,
      element: /* @__PURE__ */ jsx(Tournaments, {})
    },
    !!TournamentPage && getPromotionsCondition() && {
      path: allowedLinks.tournamentPage,
      element: /* @__PURE__ */ jsx(TournamentPage, {})
    },
    !!HallOfFame && {
      path: allowedLinks.hallOfFameCasino,
      element: /* @__PURE__ */ jsx(HallOfFame, {})
    },
    !!HallOfFame && {
      path: allowedLinks.hallOfFlame,
      element: /* @__PURE__ */ jsx(HallOfFame, {})
    }
  ].flat().filter(filterRoutes);
  return [
    {
      element: /* @__PURE__ */ jsx(Container, {}),
      children: mergeRoutes(routes, additional)
    }
  ];
};
export {
  useCreateOuterRoutes
};
